import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, FileText, Download, Mail, Phone, Building2, Package, Inbox, RefreshCw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const STATUSES = ["new", "reviewing", "quoted", "closed"];
const statusColor = {
  new: "bg-[#E53935] text-white",
  reviewing: "bg-amber-500 text-black",
  quoted: "bg-blue-500 text-white",
  closed: "bg-white/20 text-white/60",
};

export default function AdminDashboard() {
  const { user, token, logout, API } = useAuth();
  const nav = useNavigate();
  const [rfqs, setRfqs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({ total_rfqs: 0, new_rfqs: 0, total_contacts: 0 });
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("rfqs");
  const [loading, setLoading] = useState(true);

  const auth = { headers: { Authorization: `Bearer ${token}` }, withCredentials: true };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, c, s] = await Promise.all([
        axios.get(`${API}/admin/rfqs`, auth),
        axios.get(`${API}/admin/contacts`, auth),
        axios.get(`${API}/admin/stats`, auth),
      ]);
      setRfqs(r.data); setContacts(c.data); setStats(s.data);
    } catch { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, token]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (rfq_id, status) => {
    try {
      const fd = new FormData(); fd.append("status", status);
      await axios.patch(`${API}/admin/rfqs/${rfq_id}`, fd, auth);
      setRfqs((prev) => prev.map((r) => (r.rfq_id === rfq_id ? { ...r, status } : r)));
      if (selected?.rfq_id === rfq_id) setSelected({ ...selected, status });
      toast.success("Status updated");
    } catch { toast.error("Update failed"); }
  };

  const download = async (f) => {
    try {
      const res = await axios.get(`${API}/admin/files/${f.storage_path}`, { ...auth, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = f.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const doLogout = async () => { await logout(); nav("/admin/login"); };

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-white">
      {/* Top bar */}
      <div className="sticky top-0 z-40 backdrop-blur-xl bg-black/70 border-b border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 bg-[#E53935] block" />
            <span className="font-display font-extrabold tracking-tight">EXON GLOBAL</span>
            <span className="overline text-white/40 ml-3 hidden sm:inline">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-white/50 text-sm hidden sm:inline">{user?.email}</span>
            <button onClick={load} data-testid="refresh-btn" className="text-white/50 hover:text-white transition-colors"><RefreshCw size={18} /></button>
            <button onClick={doLogout} data-testid="logout-btn" className="inline-flex items-center gap-2 text-white/70 hover:text-[#E53935] transition-colors text-sm">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          {[["Total RFQs", stats.total_rfqs], ["New", stats.new_rfqs], ["Contact msgs", stats.total_contacts]].map(([l, v], i) => (
            <div key={i} className="border border-white/10 bg-[#121214] p-6" data-testid={`stat-${i}`}>
              <div className="overline text-white/40">{l}</div>
              <div className="font-display font-extrabold text-4xl tracking-tighter mt-2">{v}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-white/10">
          {[["rfqs", "RFQs", rfqs.length], ["contacts", "Messages", contacts.length]].map(([id, label, n]) => (
            <button key={id} onClick={() => { setTab(id); setSelected(null); }} data-testid={`tab-${id}`}
              className={`px-5 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${tab === id ? "border-[#E53935] text-white" : "border-transparent text-white/40 hover:text-white/70"}`}>
              {label} <span className="text-white/30">({n})</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-white/40 py-20 text-center">Loading…</div>
        ) : tab === "rfqs" ? (
          <div className="grid lg:grid-cols-12 gap-6">
            {/* List */}
            <div className="lg:col-span-5 space-y-px">
              {rfqs.length === 0 && <div className="text-white/40 py-16 text-center flex flex-col items-center gap-3"><Inbox size={32} /> No RFQs yet</div>}
              {rfqs.map((r) => (
                <button key={r.rfq_id} onClick={() => setSelected(r)} data-testid={`rfq-row-${r.rfq_id}`}
                  className={`w-full text-left border border-white/10 p-5 transition-colors ${selected?.rfq_id === r.rfq_id ? "bg-[#1a1a1d] border-[#E53935]/50" : "bg-[#121214] hover:bg-[#1a1a1d]"}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-display font-bold text-lg">{r.name}</span>
                    <span className={`overline px-2 py-0.5 ${statusColor[r.status] || "bg-white/20"}`}>{r.status}</span>
                  </div>
                  <div className="text-white/50 text-sm">{r.company || "—"} · {r.product_category || "General"}</div>
                  <div className="flex items-center gap-3 mt-2 text-white/30 text-xs">
                    <span>{new Date(r.created_at).toLocaleDateString()}</span>
                    {r.files?.length > 0 && <span className="flex items-center gap-1 text-[#E53935]"><FileText size={12} /> {r.files.length}</span>}
                  </div>
                </button>
              ))}
            </div>

            {/* Detail */}
            <div className="lg:col-span-7">
              {selected ? (
                <div className="border border-white/10 bg-[#121214] p-8" data-testid="rfq-detail">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h2 className="font-display font-extrabold text-3xl tracking-tight">{selected.name}</h2>
                      <p className="text-white/40 text-sm mt-1">{new Date(selected.created_at).toLocaleString()}</p>
                    </div>
                    <select value={selected.status} onChange={(e) => updateStatus(selected.rfq_id, e.target.value)} data-testid="status-select"
                      className="bg-[#0A0A0B] border border-white/10 px-3 py-2 text-sm outline-none focus:border-[#E53935]">
                      {STATUSES.map((s) => <option key={s} value={s} className="bg-[#0A0A0B]">{s}</option>)}
                    </select>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4 mb-6">
                    <Info icon={Mail} label="Email" value={selected.email} href={`mailto:${selected.email}`} />
                    <Info icon={Phone} label="Phone" value={selected.phone || "—"} />
                    <Info icon={Building2} label="Company" value={selected.company || "—"} />
                    <Info icon={Package} label="Product" value={selected.product_category || "—"} />
                    <Info icon={Package} label="Industry" value={selected.industry || "—"} />
                    <Info icon={Package} label="Quantity" value={selected.quantity || "—"} />
                  </div>

                  <div className="border-t border-white/10 pt-5 mb-6">
                    <div className="overline text-white/40 mb-2">Requirements</div>
                    <p className="text-white/80 whitespace-pre-wrap leading-relaxed">{selected.message}</p>
                  </div>

                  <div className="border-t border-white/10 pt-5">
                    <div className="overline text-white/40 mb-3">Attachments ({selected.files?.length || 0})</div>
                    {selected.files?.length ? (
                      <div className="space-y-2">
                        {selected.files.map((f, i) => (
                          <button key={i} onClick={() => download(f)} data-testid={`download-${i}`}
                            className="w-full flex items-center justify-between bg-[#0A0A0B] border border-white/10 hover:border-[#E53935] px-4 py-3 transition-colors group">
                            <span className="flex items-center gap-2 text-white/70 truncate">
                              <FileText size={16} className="text-[#E53935] shrink-0" />
                              <span className="truncate">{f.original_filename}</span>
                              <span className="text-white/30 text-xs shrink-0">{Math.round(f.size / 1024)} KB</span>
                            </span>
                            <Download size={16} className="text-white/40 group-hover:text-[#E53935] shrink-0" />
                          </button>
                        ))}
                      </div>
                    ) : <p className="text-white/30 text-sm">No files attached.</p>}
                  </div>
                </div>
              ) : (
                <div className="border border-dashed border-white/10 h-full min-h-[300px] flex items-center justify-center text-white/30">
                  Select an RFQ to view details
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-px">
            {contacts.length === 0 && <div className="text-white/40 py-16 text-center flex flex-col items-center gap-3"><Inbox size={32} /> No messages yet</div>}
            {contacts.map((c, i) => (
              <div key={i} className="border border-white/10 bg-[#121214] p-6" data-testid={`contact-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-display font-bold text-lg">{c.name}</span>
                  <span className="text-white/30 text-xs">{new Date(c.created_at).toLocaleDateString()}</span>
                </div>
                <div className="text-white/50 text-sm mb-3">{c.email} · {c.company || "—"}</div>
                <p className="text-white/80 whitespace-pre-wrap">{c.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Info({ icon: Icon, label, value, href }) {
  return (
    <div className="flex items-start gap-3">
      <Icon size={16} className="text-[#E53935] mt-1 shrink-0" />
      <div className="min-w-0">
        <div className="overline text-white/40">{label}</div>
        {href ? <a href={href} className="text-white hover:text-[#E53935] transition-colors break-all">{value}</a>
          : <div className="text-white break-words">{value}</div>}
      </div>
    </div>
  );
}
